import type {
  ContentObjectView,
  WithingsOrderPostContentObjectDetail,
  WithingsOrderPostContentObjectList,
} from 'models';
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from 'permissions';
import { Icon, type MenuProps, message } from 'ui';
import { useUnbookmarkObjectMutation } from 'features/bookmark';
import { useReportContentObjectModalConfirm } from 'features/report';

type P = {
  item: WithingsOrderPostContentObjectDetail | WithingsOrderPostContentObjectList;
  view: ContentObjectView;
};

type UseCreateActionsReturn = {
  actions: MenuProps['items'];
  contexts?: ReactNode[];
};

export const useCreateActions = ({ item, view }: P): UseCreateActionsReturn => {
  const { t } = useTranslation();
  const { confirm: confirmReportWithingsOrderPost, contextHolder: reportContentObjectContextHolder } =
    useReportContentObjectModalConfirm();

  const { canReportContentObject } = usePermissions();
  const { mutate: unbookmark } = useUnbookmarkObjectMutation();

  return useMemo(() => {
    if (view === 'bookmark') {
      return {
        actions: [
          {
            icon: <Icon icon="bookmark-line" size="small" />,
            key: 'unbookmark',
            label: t('Unbookmark'),
            onClick() {
              unbookmark(
                { contentObject: item, type: 'rails' },
                {
                  onError() {
                    message.error('Something went wrong.');
                  },
                  onSuccess() {
                    message.success('Successfully unbookmarked');
                  },
                },
              );
            },
          },
        ],
      };
    }

    const actions = [];

    if (canReportContentObject({ contentObject: item })) {
      actions.push({
        icon: <Icon icon="flag-1-line" size="small" />,
        key: 'report',
        label: t('Report'),
        onClick() {
          confirmReportWithingsOrderPost({
            id: item.id,
            onSuccess() {
              message.success('Successfully reported');
            },
            title: 'Are you sure you want to report this?',
          });
        },
      });
    }

    return {
      actions,
      contexts: [reportContentObjectContextHolder],
    };
  }, [
    view,
    canReportContentObject,
    item,
    reportContentObjectContextHolder,
    t,
    confirmReportWithingsOrderPost,
    unbookmark,
  ]);
};
